import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

/**
 * ClubWelcomePage - Landing page for users scanning QR code at club entrance
 * Shows welcome popup with club name & location, user clicks Proceed to go to login
 */
const ClubWelcomePage = () => {
    const { clubCode } = useParams();
    const navigate = useNavigate();
    const [clubInfo, setClubInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        const fetchClubInfo = async () => {
            try {
                setLoading(true);
                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:9000';
                const response = await axios.get(`${baseUrl}/clubs/public/${clubCode}`);

                if (response.data.success) {
                    const club = response.data.data;
                    setClubInfo(club);

                    // Store club info in sessionStorage for post-login use
                    sessionStorage.setItem('pendingWelcomeClub', JSON.stringify({
                        clubCode: club.clubCode,
                        name: club.name,
                        locationString: club.locationString,
                        welcomeMessage: club.welcomeMessage,
                        location: club.location,
                    }));

                    // Show the welcome popup after a brief delay
                    setTimeout(() => {
                        setShowPopup(true);
                    }, 500);
                }
            } catch (err) {
                console.error('Error fetching club info:', err);
                setError(err.response?.data?.message || 'Club not found');
            } finally {
                setLoading(false);
            }
        };

        if (clubCode) {
            fetchClubInfo();
        } else {
            navigate('/player-login');
        }
    }, [clubCode, navigate]);

    // Handle Proceed button click - navigate to player login
    const handleProceed = () => {
        navigate('/player-login');
    };

    return (
        <div className="club-welcome-landing">
            {/* Animated background */}
            <div className="welcome-bg-animation">
                <div className="gradient-orb orb-1"></div>
                <div className="gradient-orb orb-2"></div>
                <div className="gradient-orb orb-3"></div>
            </div>

            <div className="welcome-content">
                {/* Poker chips icon */}
                <div className="poker-icon-container">
                    <div className="poker-chip">
                        <span>♠</span>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p className="loading-text">Finding your club...</p>
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <div className="error-icon">!</div>
                        <h2>Club Not Found</h2>
                        <p>{error}</p>
                        <button className="proceed-button" onClick={handleProceed}>
                            Go to Player Login
                        </button>
                    </div>
                ) : showPopup ? (
                    <div className="welcome-popup-container">
                        <div className="popup-card">
                            <div className="popup-header">
                                <h2 className="welcome-text">Welcome to</h2>
                                <h1 className="club-name">{clubInfo?.name}</h1>
                            </div>
                            {clubInfo?.locationString && (
                                <div className="location-badge">
                                    <span className="location-icon">📍</span>
                                    <span className="location-text">{clubInfo.locationString}</span>
                                </div>
                            )}
                            {clubInfo?.welcomeMessage && (
                                <p className="welcome-message">{clubInfo.welcomeMessage}</p>
                            )}
                            <button className="proceed-button" onClick={handleProceed}>
                                <span>Proceed</span>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p className="loading-text">Preparing your experience...</p>
                    </div>
                )}
            </div>

            <style jsx="true">{`
        .club-welcome-landing {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--cm-bg);
          color: var(--cm-text);
          position: relative;
          overflow: hidden;
          padding: 20px;
        }

        /* Animated background orbs */
        .welcome-bg-animation {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: hidden;
          z-index: 0;
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.5;
          animation: float 8s ease-in-out infinite;
        }

        .orb-1 {
          width: 400px;
          height: 400px;
          background: var(--cm-red-soft);
          top: -100px;
          left: -100px;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 300px;
          height: 300px;
          background: var(--cm-gold-soft);
          bottom: -50px;
          right: -50px;
          animation-delay: -2s;
        }

        .orb-3 {
          width: 250px;
          height: 250px;
          background: var(--cm-gold-soft);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -4s;
        }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.05); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
        }

        /* Main content container */
        .welcome-content {
          position: relative;
          z-index: 1;
          text-align: center;
          max-width: 500px;
          width: 100%;
          animation: fadeInUp 0.6s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Poker chip icon */
        .poker-icon-container {
          margin-bottom: 30px;
        }

        .poker-chip {
          width: 80px;
          height: 80px;
          margin: 0 auto;
          background: var(--cm-red);
          border: 6px dashed rgba(255, 255, 255, 0.85);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: var(--cm-shadow-md);
          animation: pulse 2s ease-in-out infinite;
        }

        .poker-chip span {
          font-size: 36px;
          color: #ffffff;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 8px var(--cm-red-soft); }
          50% { transform: scale(1.05); box-shadow: 0 0 0 14px var(--cm-red-soft); }
        }

        /* Loading state */
        .loading-state {
          color: var(--cm-text);
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid var(--cm-border);
          border-left-color: var(--cm-gold);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-text {
          font-size: 18px;
          color: var(--cm-text);
        }

        /* Error state */
        .error-state {
          color: var(--cm-text);
        }

        .error-icon {
          width: 60px;
          height: 60px;
          background: var(--cm-surface);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          font-weight: bold;
          margin: 0 auto 20px;
        }

        .error-state h2 {
          font-size: 24px;
          margin-bottom: 10px;
        }

        .error-state p {
          color: var(--cm-text);
        }

        /* Welcome popup container */
        .welcome-popup-container {
          animation: popupFadeIn 0.5s ease-out;
        }

        @keyframes popupFadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .popup-card {
          background: var(--cm-surface);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 40px 30px;
          border: 1px solid var(--cm-border);
          box-shadow: var(--cm-shadow-md);
        }

        .popup-header {
          margin-bottom: 20px;
        }

        .welcome-text {
          font-size: 18px;
          color: var(--cm-text);
          margin-bottom: 5px;
          font-weight: 400;
        }

        .club-name {
          font-size: 36px;
          font-weight: 700;
          margin-bottom: 15px;
          background: linear-gradient(135deg, var(--cm-text) 0%, var(--cm-gold) 50%, var(--cm-text) 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        @keyframes shimmer {
          to { background-position: 200% center; }
        }

        .location-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--cm-surface-2);
          backdrop-filter: blur(10px);
          padding: 10px 20px;
          border-radius: 30px;
          margin-bottom: 20px;
          border: 1px solid var(--cm-border);
        }

        .location-icon {
          font-size: 18px;
          animation: pinBounce 1s ease-in-out infinite;
        }

        @keyframes pinBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }

        .location-text {
          font-size: 16px;
          color: var(--cm-text);
        }

        .welcome-message {
          font-size: 14px;
          color: var(--cm-text);
          margin-bottom: 25px;
          font-style: italic;
        }

        /* Proceed button */
        .proceed-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: var(--cm-gold);
          color: var(--cm-gold-ink);
          border: none;
          padding: 14px 32px;
          border-radius: 30px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: var(--cm-shadow-md);
          margin-top: 10px;
        }

        .proceed-button:hover {
          transform: translateY(-2px);
          box-shadow: var(--cm-shadow-md);
        }

        .proceed-button:active {
          transform: translateY(0);
        }

        .proceed-button svg {
          transition: transform 0.3s ease;
        }

        .proceed-button:hover svg {
          transform: translateX(4px);
        }

        /* Responsive */
        @media (max-width: 480px) {
          .club-name {
            font-size: 28px;
          }
          
          .poker-chip {
            width: 60px;
            height: 60px;
          }
          
          .poker-chip span {
            font-size: 28px;
          }
        }
      `}</style>
        </div>
    );
};

export default ClubWelcomePage;
